/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useStatusBadgeColors} from '@ion/dls/src/hooks/useStatusBadgeColors';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {
  CANCELLING_STATUSES,
  JobStatus,
  jobStatusLabel,
  NEGATIVE_STATUSES,
  POSITIVE_STATUSES
} from '../jobStatusBadge';

export interface JobStatusCellProps {
  status: string;
  progress?: {
    reason?: string | null,
    status: string
  }
};

type JobStatusCellStyles = {
  backgroundColor: string,
  textColor: string
};

export const useStyles = makeStyles((theme: DLSTheme) => ({
  container : {
    height         : '100%',
    width          : '100%',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'left'
  },
  tooltipCell : {
    cursor : 'pointer'
  },
  foundation : {
    display         : 'flex',
    backgroundColor : dlsColor('white'),
    boxSizing       : 'border-box',
    outline         : `1px solid ${dlsColor('grey6')}50`,
    borderRadius    : '4px'
  },
  cell : {
    ...dlsAlt('typography', 'label3'),
    color           : ({textColor}: JobStatusCellStyles) => textColor,
    backgroundColor : ({backgroundColor}: JobStatusCellStyles) => backgroundColor,
    fontWeight      : 500,
    fontSize        : '10px',
    lineHeight      : '16px',
    borderRadius    : '2px',
    padding         : '4px 8px',
    maxHeight       : '24px',
    minWidth        : '90px',
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center'
  }
}));

/**
 * @JobStatusCell
 * 
 * This component renders a styled cell displaying the current status of a job, task, or device. 
 * The styling and content of the cell (background color, text color, opacity, and label) 
 * are dynamically determined based on the provided job status.
 * 
 * Example usage:
 * ```typescript
 * import JobStatusCell from './JobStatusCell';
 * 
 * return (
 *   <JobStatusCell status="InProgress" />
 * );
 * ```
 * This would render a job status cell with the "In Progress" status.
 * 
 * @param status - The current status of the job, represented as a string. The possible statuses are 
 *   'Pending', 'InProgress', 'Completed', 'Failed', and 'Cancelled'. This determines the cell's 
 *   appearance and label.
 * 
 * Status Appearance (shared palette with the Device Files grid badges):
 * - **Cancelled / Cancelling / In Progress Cancelled**: Orange text on an orange tint
 * - **Completed / Acked / Partial Success**: Green text on a green tint
 * - **Failed / Nacked**: Red text on a red tint
 * - **All others, including Pending / In Progress / Sending / Retry**: Grey on a grey tint
 * 
 * @returns A styled cell displaying the job's current status.
 */
export const JobStatusCell = ({status, progress}: JobStatusCellProps) => {
  const label = useMemo(() => jobStatusLabel(status), [status]);

  const {color, textColor} = useStatusBadgeColors({
    status,
    positiveStatuses : POSITIVE_STATUSES,
    negativeStatuses : NEGATIVE_STATUSES,
    pendingStatuses  : CANCELLING_STATUSES
  });

  const classes     = useStyles({backgroundColor: color, textColor});
  const failedReason = status?.toLowerCase() === JobStatus.FAILED.toLowerCase() ? progress?.reason : '';

  return (
    <div className={classes.container}>
      <div className={classes.foundation}>
        <Render>
          <Render.If condition={!!failedReason}>
            <Tooltip arrow title={<span>{failedReason}</span>}>
              <div className={`${classes.cell} ${classes.tooltipCell}`}>
                {label}
              </div>
            </Tooltip>
          </Render.If>
          <Render.Else>
            <div className={classes.cell}>
              {label}
            </div>
          </Render.Else>
        </Render>
      </div>
    </div>
  );
};

export default memo(JobStatusCell);
